"use client"

import * as React from "react"
import { useState, useRef, useEffect } from "react"
import { cn } from "../../../lib/utils"

interface Tab {
  id: string
  label: string
}

interface TabsProps extends React.HTMLAttributes<HTMLDivElement> {
  tabs: Tab[]
  activeTab?: string
  onTabChange?: (tabId: string) => void
}

const Tabs = React.forwardRef<HTMLDivElement, TabsProps>(
  ({ className, tabs, activeTab, onTabChange, ...props }, ref) => {
    const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)
    const [activeIndex, setActiveIndex] = useState(() => {
      return activeTab ? tabs.findIndex(tab => tab.id === activeTab) : 0
    })
    const [hoverStyle, setHoverStyle] = useState({})
    const [activeStyle, setActiveStyle] = useState({ left: "0px", width: "0px", top: "0px" })
    const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 1200)
    const tabRefs = useRef<(HTMLDivElement | null)[]>([])
    const containerRef = useRef<HTMLDivElement>(null)

    const updatePositions = () => {
      // Update active tab position
      const activeElement = tabRefs.current[activeIndex]
      if (activeElement && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const elementRect = activeElement.getBoundingClientRect()
        const relativeLeft = elementRect.left - containerRect.left
        const relativeTop = elementRect.top - containerRect.top
        
        setActiveStyle({
          left: `${relativeLeft}px`,
          width: `${elementRect.width}px`,
          top: `${relativeTop + elementRect.height - 2}px`,
        })
      }
      
      // Update hover position if hovering
      if (hoveredIndex !== null) {
        const hoveredElement = tabRefs.current[hoveredIndex]
        if (hoveredElement && containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect()
          const elementRect = hoveredElement.getBoundingClientRect()
          const relativeLeft = elementRect.left - containerRect.left
          const relativeTop = elementRect.top - containerRect.top
          
          setHoverStyle({
            left: `${relativeLeft}px`,
            width: `${elementRect.width}px`,
            top: `${relativeTop}px`,
          })
        }
      }
    }

    useEffect(() => {
      if (hoveredIndex !== null) {
        const hoveredElement = tabRefs.current[hoveredIndex]
        if (hoveredElement && containerRef.current) {
          const containerRect = containerRef.current.getBoundingClientRect()
          const elementRect = hoveredElement.getBoundingClientRect()
          const relativeLeft = elementRect.left - containerRect.left
          const relativeTop = elementRect.top - containerRect.top
          
          setHoverStyle({
            left: `${relativeLeft}px`,
            width: `${elementRect.width}px`,
            top: `${relativeTop}px`,
          })
        }
      }
    }, [hoveredIndex])

    useEffect(() => {
      const activeElement = tabRefs.current[activeIndex]
      if (activeElement && containerRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()
        const elementRect = activeElement.getBoundingClientRect()
        const relativeLeft = elementRect.left - containerRect.left
        const relativeTop = elementRect.top - containerRect.top
        
        setActiveStyle({
          left: `${relativeLeft}px`,
          width: `${elementRect.width}px`,
          top: `${relativeTop + elementRect.height - 2}px`,
        })
      }
    }, [activeIndex])

    useEffect(() => {
      const timeoutId = setTimeout(() => {
        updatePositions()
      }, 100)
      
      return () => clearTimeout(timeoutId)
    }, [activeIndex, hoveredIndex])

    useEffect(() => {
      const handleResize = () => {
        setWindowWidth(window.innerWidth)
        updatePositions()
      }
      
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [activeIndex, hoveredIndex])

    useEffect(() => {
      if (activeTab) {
        const newIndex = tabs.findIndex(tab => tab.id === activeTab)
        if (newIndex !== -1 && newIndex !== activeIndex) {
          setActiveIndex(newIndex)
        }
      }
    }, [activeTab, tabs])

    useEffect(() => {
      requestAnimationFrame(() => {
        updatePositions()
      })
    }, [])

    useEffect(() => {
      requestAnimationFrame(() => {
        updatePositions()
      })
    }, [tabs.length])

    return (
      <div 
        ref={ref} 
        className={cn("tw-relative", className)} 
        {...props}
      >
        <div className="tw-relative" ref={containerRef}>
          <div
            className="tw-absolute tw-h-[30px] tw-transition-all tw-duration-300 tw-ease-out tw-bg-[#0e0f1114] dark:tw-bg-[#ffffff1a] tw-rounded-[6px] tw-flex tw-items-center tw-z-0 tw-pointer-events-none"
            style={{
              ...hoverStyle,
              opacity: hoveredIndex !== null ? 1 : 0,
              pointerEvents: 'none'
            }}
          />

          <div
            className="tw-absolute tw-h-[2px] tw-bg-[#2e9fd0] dark:tw-bg-white tw-transition-all tw-duration-300 tw-ease-out tw-z-0 tw-pointer-events-none"
            style={{
              ...activeStyle,
              pointerEvents: 'none'
            }}
          />

          <div 
            className="tw-relative tw-flex tw-flex-wrap tw-gap-[8px] tw-items-start tw-mt-[5px] tw-justify-start tabs-container-responsive"
            style={windowWidth <= 674 ? {
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: '0.5em',
              alignItems: 'start'
            } : {}}
          >
            {tabs.map((tab, index) => (
              <div
                key={tab.id}
                ref={(el) => (tabRefs.current[index] = el)}
                className={cn(
                  "tw-px-2 tw-py-2 tw-cursor-pointer tw-transition-colors tw-duration-300 tw-h-[30px] tw-min-w-0 tw-flex-shrink-0 tw-relative tw-z-10 tab-item-responsive",
                  index === activeIndex 
                    ? "tw-text-[#0e0e10] dark:tw-text-white" 
                    : "tw-text-[#0e0f1199] dark:tw-text-[#ffffff99]"
                )}
                style={windowWidth <= 674 ? {
                  width: '100%',
                  textAlign: 'center',
                  boxSizing: 'border-box',
                  justifyContent: 'center',
                  display: 'flex',
                  alignItems: 'center'
                } : {}}
                onMouseEnter={() => setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
                onClick={() => {
                  setActiveIndex(index)
                  onTabChange?.(tab.id)
                }}
              >
                <div className="tw-text-sm tw-font-medium tw-leading-5 tw-whitespace-nowrap tw-flex tw-items-center tw-justify-center tw-h-full tab-title tw-text-center">
                  {tab.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }
)
Tabs.displayName = "Tabs"

export { Tabs }
